import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { FechaRelativa } from "@/components/domain";
import { Button } from "@/components/ui/button";
import { notificacionesApi } from "@/lib/api/notificaciones";
import { cn } from "@/lib/utils/cn";

const STALE_MS = 30_000;
const MAX_VISIBLES = 10;
/**
 * Polling de fallback. Con SSE conectado (ADR 018) el invalidate es
 * instantáneo — este polling solo cubre desconexiones largas o navegadores
 * que pausan JS en pestañas inactivas. Cinco minutos balancea "detectar
 * drift razonable" con "no malgastar ciclos cuando todo está bien".
 */
const POLL_FALLBACK_MS = 5 * 60_000;

/**
 * Campana de notificaciones del topbar (Fase 5 §F5-012, ADR 018).
 *
 * - **Real-time vía SSE.** El hook `useSse` (montado en AppShell) invalida
 *   `["notificaciones", "mias"]` apenas llega un evento `notificacion.nueva`.
 *   El refetch lo dispara TanStack Query automáticamente.
 * - **Polling de fallback cada 5 min.** Si el SSE cae o el browser pausó
 *   el JS, esto detecta drift. No es la fuente primaria — solo un seguro.
 * - Badge con contador no-leídas; capped a "9+" para no romper layout.
 * - Dropdown con las últimas 10 notificaciones no leídas.
 * - Click en una notificación: marca como leída + navega al expediente
 *   (si tiene uno asociado) + cierra el dropdown.
 * - Cierre por click fuera del componente (handler montado en `document`).
 */
export function NotificacionesCampana() {
  const [abierto, setAbierto] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["notificaciones", "mias"],
    queryFn: () => notificacionesApi.mias(),
    staleTime: STALE_MS,
    refetchInterval: POLL_FALLBACK_MS,
  });

  const marcarLeida = useMutation({
    mutationFn: (id: string) => notificacionesApi.marcarLeida(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notificaciones", "mias"] });
    },
  });

  // Cierra al hacer click fuera del componente.
  useEffect(() => {
    if (!abierto) return;
    const handler = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setAbierto(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [abierto]);

  const items = query.data ?? [];
  const noLeidas = items.length;
  const contador = noLeidas > 9 ? "9+" : String(noLeidas);

  const handleSelect = (notifId: string, expedienteId: string | null): void => {
    marcarLeida.mutate(notifId);
    setAbierto(false);
    if (expedienteId) navigate(`/expedientes/${expedienteId}`);
  };

  return (
    <div className="relative" ref={rootRef}>
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        aria-label={noLeidas > 0 ? `${noLeidas} notificaciones no leídas` : "Notificaciones"}
        onClick={() => setAbierto((v) => !v)}
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        {noLeidas > 0 ? (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-state-vencido px-1 text-[10px] font-bold text-white"
            aria-hidden="true"
          >
            {contador}
          </span>
        ) : null}
      </Button>

      {abierto ? (
        <div
          role="dialog"
          aria-label="Centro de notificaciones"
          className={cn(
            "absolute right-0 z-50 mt-2 w-80 max-w-[95vw] overflow-hidden rounded-lg border border-border bg-card shadow-lg",
          )}
        >
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <p className="text-sm font-semibold">Notificaciones</p>
            <span className="text-xs text-muted-foreground">{noLeidas} no leídas</span>
          </div>

          {query.isPending ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">Cargando…</p>
          ) : items.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              No tienes notificaciones nuevas.
            </p>
          ) : (
            <ul className="max-h-96 divide-y divide-border overflow-y-auto">
              {items.slice(0, MAX_VISIBLES).map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(n.id, n.expedienteId)}
                    className="flex w-full flex-col gap-1 px-3 py-2 text-left text-sm hover:bg-muted/40"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="line-clamp-1 font-medium text-foreground">{n.asunto}</span>
                      <Check
                        className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                    </div>
                    <p className="line-clamp-2 text-xs text-muted-foreground">{n.contenido}</p>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      <FechaRelativa fecha={n.fechaCreacion} />
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
